import { useEffect, useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SearchWidget from "@/components/SearchWidget";
import VehicleCard from "@/components/VehicleCard";
import { api, API_BASE } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { useI18n } from "@/lib/i18n";
import { ShieldCheck, Plane, MessageCircle, Lock, MapPin, ArrowRight, Star } from "lucide-react";
import { Link } from "react-router-dom";

const DEFAULT_HERO = "https://images.pexels.com/photos/5054166/pexels-photo-5054166.jpeg";

function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/uploads/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

export default function Home() {
  const { settings } = useSettings();
  const { t } = useI18n();
  const [vehicles, setVehicles] = useState([]);
  const [agencies, setAgencies] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data.slice(0, 8))).catch(() => {});
    api.get("/agencies").then((r) => setAgencies(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const hero = resolveUrl(settings.banner_url) || DEFAULT_HERO;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="relative">
        <div className="absolute inset-0 z-0">
          <img src={hero} alt="Bannière" className="w-full h-full object-cover" />
          <div className="absolute inset-0 mbk-hero-overlay" />
        </div>
        <div className="relative z-10">
          <SiteHeader transparent />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32 md:pt-24 md:pb-48">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 px-4 py-1.5 rounded-full text-white text-xs uppercase tracking-[0.2em] font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-[#d4a437]" /> {t("home_badge")}
              </div>
              <h1 className="mt-6 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {t("home_title_1")}<br />
                <span className="text-[#d4a437]">{t("home_title_2")}</span>
              </h1>
              <p className="mt-5 text-white/85 text-base md:text-lg max-w-xl">{t("home_subtitle")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 md:-mt-32 relative z-20">
        <SearchWidget />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, title: t("home_feat_verified"), sub: t("home_feat_verified_sub") },
            { icon: Plane, title: t("home_feat_early"), sub: t("home_feat_early_sub") },
            { icon: MessageCircle, title: t("home_feat_wa"), sub: t("home_feat_wa_sub") },
            { icon: Lock, title: t("home_feat_pay"), sub: t("home_feat_pay_sub") },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 mbk-card-shadow">
              <f.icon className="h-6 w-6 text-[#0e7a3f]" />
              <div className="mt-3 font-semibold text-[#0a1e3f]">{f.title}</div>
              <div className="text-xs text-gray-500 mt-1">{f.sub}</div>
            </div>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0e7a3f] font-bold">{t("home_fleet")}</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a1e3f] mt-1">{t("home_available")}</h2>
          </div>
          <Link to="/search" data-testid="see-all-vehicles" className="text-sm font-semibold text-[#0a1e3f] hover:text-[#0e7a3f] inline-flex items-center gap-1">
            {t("home_see_all")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {vehicles.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
            <p className="text-gray-500">{t("home_empty_fleet")}</p>
            <p className="text-xs text-gray-400 mt-2">{t("home_empty_fleet_sub")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0e7a3f] font-bold">{t("home_network")}</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a1e3f] mt-1">{t("home_agencies_title")}</h2>
          </div>
        </div>
        {agencies.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
            <p className="text-gray-500">{t("home_empty_agencies")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {agencies.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl p-5 border border-gray-100 mbk-card-shadow flex items-center gap-3">
                {a.logo_url ? (
                  <img src={a.logo_url} className="h-12 w-12 rounded-full object-cover" alt={a.name} />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-[#0a1e3f] text-[#d4a437] font-black flex items-center justify-center">
                    {a.name?.[0]}
                  </div>
                )}
                <div>
                  <div className="font-semibold text-[#0a1e3f]">{a.name}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.city}</div>
                  <div className="text-xs text-[#0e7a3f] font-semibold mt-0.5">{a.vehicle_count} véhicules</div>
                  {a.rating > 0 && (
                    <div className="text-xs text-[#0a1e3f] font-bold flex items-center gap-1 mt-0.5" data-testid={`home-agency-rating-${a.id}`}>
                      <Star className="h-3 w-3 fill-[#d4a437] text-[#d4a437]" />
                      {Number(a.rating).toFixed(1)}
                      <span className="text-gray-400 font-normal">({a.reviews_count || 0})</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 mb-8">
        <div className="bg-[#0a1e3f] rounded-3xl p-10 md:p-16 text-white relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#d4a437]/20" />
          <div className="absolute -right-10 -bottom-10 h-48 w-48 rounded-full bg-[#0e7a3f]/20" />
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#d4a437] font-bold">{t("home_cta_partner_badge")}</div>
            <h3 className="font-display text-3xl md:text-4xl font-black mt-2">{t("home_cta_partner_title")}</h3>
            <p className="mt-4 text-white/80">{t("home_cta_partner_sub")}</p>
            <Link to="/register" data-testid="cta-partner" className="mt-6 inline-flex items-center gap-2 mbk-btn-gold px-6 py-3 font-semibold">
              {t("home_cta_partner_btn")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
